// Types shared between client and server.

export type UserRole = 'technician' | 'manager' | 'admin' | 'leadership';

/**
 * What a users row may hold. 'viewer' is a view-only login: it is never an
 * effective role. `toEffectiveRole` turns it into 'manager' with `viewOnly`
 * set, so every read path treats it as a manager and the server refuses its
 * writes in one place (protectedProcedure).
 */
export type StoredUserRole = UserRole | 'viewer';

export function toEffectiveRole(stored: string): { role: UserRole; viewOnly: boolean } {
  if (stored === 'viewer') return { role: 'manager', viewOnly: true };
  return { role: stored as UserRole, viewOnly: false };
}

export interface AppUser {
  id: string;
  companyId: string;
  primaryLocationId: string | null;
  role: UserRole;
  /** A view-only login (stored role 'viewer'): manager reads, no writes. */
  viewOnly: boolean;
  email: string | null;
  phone: string | null;
  firstName: string;
  lastName: string;
  employeeId: string | null;
  preferredLanguage: string;
  shiftEndTime: string;
  disabled: boolean;
  /** Fleetwise AI owners (Isaac, Shane) — gated impersonation + review. */
  isPlatformOwner: boolean;
}

export type ThreeCGeneratedBy = 'roldi' | 'manual' | 'roldi_edited';
export type TaskStatus = 'active' | 'complete' | 'cancelled';
export type MessageRole = 'user' | 'assistant' | 'system' | 'tool';
export type ConversationScope = 'quick_chat' | 'task' | 'truck_history' | 'shop_channel' | 'dm';

export interface ImpersonationSession {
  actingUserId: string;
  targetUserId: string;
  expiresAt: string;
  accessToken: string;
  refreshToken: string;
}

export const FEATURE_FLAG_KEYS = [
  'roldi_enabled',
  'shop_chat_enabled',
  'warranty_flagging',
  'safety_critical_gating',
  'spanish_support',
  'photo_ocr',
  'offline_compose_queue',
] as const;
export type FeatureFlagKey = (typeof FEATURE_FLAG_KEYS)[number];
